import { Link, useNavigate } from "@tanstack/react-router";
import { LayoutDashboard, LogOut, Shield, ShoppingBag, Sparkles, User as UserIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSubscriptions } from "../hooks/useSubscriptions";
import { signOut, useSession } from "../lib/auth-client";

/** Build up-to-two-letter initials from a name, falling back to the email. */
export function initialsFrom(name?: string | null, email?: string | null): string {
	if (name?.trim()) {
		return name
			.trim()
			.split(/\s+/)
			.slice(0, 2)
			.map((part) => part[0])
			.join("")
			.toUpperCase();
	}
	if (email) return email[0].toUpperCase();
	return "?";
}

type Variant = "avatar" | "pill";

/**
 * Top-right user control. Reads the better-auth session:
 * - loading  → skeleton circle (no layout shift)
 * - logged out → "Logg inn" link to /login
 * - logged in  → initials avatar opening a dropdown (account, purchases, logout)
 *
 * `variant="avatar"` (default) is the compact circle for page headers.
 * `variant="pill"` is the full-width avatar + name/email used at the bottom of
 * the app sidebar; its dropdown opens upward.
 */
export function UserMenu({ variant = "avatar" }: { variant?: Variant }) {
	const { data: session, isPending } = useSession();
	const { isPro } = useSubscriptions();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		function onClick(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
		}
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") setOpen(false);
		}
		document.addEventListener("mousedown", onClick);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onClick);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	if (isPending) {
		return (
			<div
				className={
					variant === "pill"
						? "h-12 w-full animate-pulse rounded-xl bg-sunken"
						: "h-8 w-8 animate-pulse rounded-full bg-sunken"
				}
				aria-hidden="true"
			/>
		);
	}

	const user = session?.user;
	if (!user) {
		if (variant === "pill") {
			return (
				<Link to="/login" className="btn-secondary w-full justify-center">
					Logg inn
				</Link>
			);
		}
		return (
			<Link to="/login" className="btn-secondary btn-sm">
				Logg inn
			</Link>
		);
	}

	const isAdmin = (user as { role?: string }).role === "admin";
	const initials = initialsFrom(user.name, user.email);

	async function handleSignOut() {
		setOpen(false);
		await signOut();
		navigate({ to: "/" });
	}

	const avatarCircle = (
		<span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink text-sm font-semibold text-canvas">
			{initials}
		</span>
	);

	return (
		<div className={variant === "pill" ? "relative w-full" : "relative"} ref={ref}>
			{variant === "pill" ? (
				<button
					type="button"
					onClick={() => setOpen((v) => !v)}
					aria-haspopup="menu"
					aria-expanded={open}
					className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-sunken"
				>
					{avatarCircle}
					<span className="min-w-0 flex-1">
						<span className="block truncate text-sm font-medium text-ink">{user.name || user.email}</span>
						{user.name && <span className="block truncate text-xs text-muted">{user.email}</span>}
					</span>
				</button>
			) : (
				<button
					type="button"
					onClick={() => setOpen((v) => !v)}
					aria-haspopup="menu"
					aria-expanded={open}
					aria-label="Brukermeny"
					className="ml-1 flex h-8 w-8 items-center justify-center rounded-full bg-ink text-[13px] font-semibold text-canvas transition-opacity hover:opacity-85"
				>
					{initials}
				</button>
			)}

			{open && (
				<div
					role="menu"
					className={`float absolute z-50 overflow-hidden rounded-xl animate-fade-in ${
						variant === "pill" ? "bottom-full left-0 mb-2 w-full" : "right-0 mt-2 w-60"
					}`}
				>
					<div className="border-b border-line px-4 py-3">
						{user.name && <p className="truncate text-sm font-semibold text-ink">{user.name}</p>}
						<p className="truncate text-xs text-muted">{user.email}</p>
					</div>
					<div className="py-1">
						<Link
							to="/account"
							onClick={() => setOpen(false)}
							role="menuitem"
							className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-2 transition-colors hover:bg-sunken hover:text-ink"
						>
							<UserIcon className="h-4 w-4" />
							Min konto
						</Link>
						<Link
							to="/account"
							hash="steder"
							onClick={() => setOpen(false)}
							role="menuitem"
							className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-2 transition-colors hover:bg-sunken hover:text-ink"
						>
							<ShoppingBag className="h-4 w-4" />
							Mine steder
						</Link>
						{isPro ? (
							<Link
								to="/account/konsoll"
								onClick={() => setOpen(false)}
								role="menuitem"
								className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-2 transition-colors hover:bg-sunken hover:text-ink"
							>
								<LayoutDashboard className="h-4 w-4" />
								Konsoll
							</Link>
						) : (
							<Link
								to="/pricing"
								onClick={() => setOpen(false)}
								role="menuitem"
								className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-sun-text transition-colors hover:bg-sunken"
							>
								<Sparkles className="h-4 w-4" />
								Bli Pro
							</Link>
						)}
					</div>
					{isAdmin && (
						<div className="border-t border-line py-1">
							<Link
								to="/account/admin"
								onClick={() => setOpen(false)}
								role="menuitem"
								className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-2 transition-colors hover:bg-sunken hover:text-ink"
							>
								<Shield className="h-4 w-4" />
								Admin
							</Link>
						</div>
					)}
					<div className="border-t border-line py-1">
						<button
							type="button"
							onClick={handleSignOut}
							role="menuitem"
							className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-ink-2 transition-colors hover:bg-sunken hover:text-danger"
						>
							<LogOut className="h-4 w-4" />
							Logg ut
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
